import { EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES } from 'common/constants';
import { isValuePresent } from 'helpers/utils';
import * as yup from 'yup';

import { PAYROLL_WORKSHEET_FIELD_CODES } from '../PayrollWorksheetContainer';

/** Every field rendered only inside PayrollWorksheetContainer (exclude from main-page dynamic validation). */
export const PAYROLL_WORKSHEET_CONTAINER_FIELD_CODES: readonly string[] = [
  ...Object.values(PAYROLL_WORKSHEET_FIELD_CODES),
  ...Object.values(EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES),
];

/** Field codes for payroll worksheet Full Time and Part Time input fields (excludes computed/readonly) */
export const PAYROLL_WORKSHEET_INPUT_FIELD_CODES = [
  'fullTimeEmployeeCount',
  'fullTimeAvgHourlyWage',
  'fullTimeAvgWeeklyHours',
  'partTimeEmployeeCount',
  'partTimeAvgHourlyWage',
  'partTimeAvgWeeklyHours',
] as const;

type PayrollFormValues = Record<string, string>;

const makeGroupFieldValidator = (
  testName: string,
  getFtValues: (value: string, parent: PayrollFormValues) => (string | undefined)[],
  getPtValues: (value: string, parent: PayrollFormValues) => (string | undefined)[],
  isPartTime: boolean,
  requiredMessage: string,
  employeeCountField?: string,
  skipWhen?: (parent: PayrollFormValues) => boolean,
): yup.StringSchema =>
  yup.string().test(testName, requiredMessage, function validatePayrollField(value) {
    const parent: PayrollFormValues = (this as yup.TestContext).parent ?? {};

    if (skipWhen?.(parent)) {
      return true;
    }

    if (parent.payrollWorksheet !== 'Yes') return true;
    if (employeeCountField && parent[employeeCountField] === '0') return true;

    const ftAnyFilled = getFtValues(value ?? '', parent).some((v) => isValuePresent(v));
    const ptAnyFilled = getPtValues(value ?? '', parent).some((v) => isValuePresent(v));
    const isRequired = isPartTime ? ptAnyFilled || !ftAnyFilled : ftAnyFilled || !ptAnyFilled;
    return !isRequired || isValuePresent(value);
  });

/**
 * Returns yup validation schemas for payroll worksheet FT/PT input fields.
 * - At least one group (FT or PT) must be filled.
 * - When any of the 3 FT fields has value, all 3 FT fields become required.
 * - When any of the 3 PT fields has value, all 3 PT fields become required.
 * - When neither group has data, all 6 fields show validation errors.
 * - Expanded hotel + isSeasonalBusiness=No: standard seasonal validation only.
 * - Expanded hotel + isSeasonalBusiness=Yes: seasonal validated + hasYearRoundEmployees required;
 *   when hasYearRoundEmployees=Yes, year-round FT/PT groups use the same rules.
 */
export const getPayrollWorksheetValidations = (
  isExpandedHotel: boolean,
): Record<string, yup.StringSchema> => {
  const requiredMessage = 'This field cannot be left blank.';

  const ftValues = (parent: PayrollFormValues): (string | undefined)[] => [
    parent.fullTimeEmployeeCount,
    parent.fullTimeAvgHourlyWage,
    parent.fullTimeAvgWeeklyHours,
  ];
  const ptValues = (parent: PayrollFormValues): (string | undefined)[] => [
    parent.partTimeEmployeeCount,
    parent.partTimeAvgHourlyWage,
    parent.partTimeAvgWeeklyHours,
  ];

  const skipYearRound: (parent: PayrollFormValues) => boolean = (parent) =>
    parent.isSeasonalBusiness !== 'Yes' || parent.hasYearRoundEmployees !== 'Yes';

  const yrFtValues = (parent: PayrollFormValues): (string | undefined)[] => [
    parent.yearRoundFtEmployeeCount,
    parent.yearRoundFtAvgHourlyWage,
    parent.yearRoundFtAvgWeeklyHours,
  ];
  const yrPtValues = (parent: PayrollFormValues): (string | undefined)[] => [
    parent.yearRoundPtEmployeeCount,
    parent.yearRoundPtAvgHourlyWage,
    parent.yearRoundPtAvgWeeklyHours,
  ];

  const commonValidations: Record<string, yup.StringSchema> = {
    isSeasonalBusiness: yup
      .string()
      .test(
        'seasonal-business-required',
        'Select Yes or No.',
        function validateSeasonalBusiness(value) {
          const parent: PayrollFormValues = (this as yup.TestContext).parent ?? {};
          if (parent.payrollWorksheet !== 'Yes') return true;
          return value === 'Yes' || value === 'No';
        },
      ),
    operationalMonths: yup
      .string()
      .test(
        'operational-months-required',
        requiredMessage,
        function validateOperationalMonths(value) {
          const parent: PayrollFormValues = (this as yup.TestContext).parent ?? {};
          if (parent.payrollWorksheet !== 'Yes') return true;
          if (parent.isSeasonalBusiness !== 'Yes') return true;
          return isValuePresent(value);
        },
      ),
  };

  const seasonal: Record<string, yup.StringSchema> = {
    fullTimeEmployeeCount: makeGroupFieldValidator(
      'payroll-ft-count',
      (value, parent) => [value, parent.fullTimeAvgHourlyWage, parent.fullTimeAvgWeeklyHours],
      (_, parent) => ptValues(parent),
      false,
      requiredMessage,
    ),
    fullTimeAvgHourlyWage: makeGroupFieldValidator(
      'payroll-ft-wage',
      (value, parent) => [parent.fullTimeEmployeeCount, value, parent.fullTimeAvgWeeklyHours],
      (_, parent) => ptValues(parent),
      false,
      requiredMessage,
      'fullTimeEmployeeCount',
    ),
    fullTimeAvgWeeklyHours: makeGroupFieldValidator(
      'payroll-ft-hours',
      (value, parent) => [parent.fullTimeEmployeeCount, parent.fullTimeAvgHourlyWage, value],
      (_, parent) => ptValues(parent),
      false,
      requiredMessage,
      'fullTimeEmployeeCount',
    ),
    partTimeEmployeeCount: makeGroupFieldValidator(
      'payroll-pt-count',
      (_, parent) => ftValues(parent),
      (value, parent) => [value, parent.partTimeAvgHourlyWage, parent.partTimeAvgWeeklyHours],
      true,
      requiredMessage,
    ),
    partTimeAvgHourlyWage: makeGroupFieldValidator(
      'payroll-pt-wage',
      (_, parent) => ftValues(parent),
      (value, parent) => [parent.partTimeEmployeeCount, value, parent.partTimeAvgWeeklyHours],
      true,
      requiredMessage,
      'partTimeEmployeeCount',
    ),
    partTimeAvgWeeklyHours: makeGroupFieldValidator(
      'payroll-pt-hours',
      (_, parent) => ftValues(parent),
      (value, parent) => [parent.partTimeEmployeeCount, parent.partTimeAvgHourlyWage, value],
      true,
      requiredMessage,
      'partTimeEmployeeCount',
    ),
  };

  if (!isExpandedHotel) {
    return { ...commonValidations, ...seasonal };
  }

  const yearRound: Record<string, yup.StringSchema> = {
    [EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.hasYearRoundEmployees]: yup
      .string()
      .test(
        'has-year-round-conditional',
        'Select Yes or No.',
        function validateHasYearRound(value) {
          const parent: PayrollFormValues = (this as yup.TestContext).parent ?? {};
          if (parent.payrollWorksheet !== 'Yes') return true;
          if (parent.isSeasonalBusiness !== 'Yes') return true;
          return value === 'Yes' || value === 'No';
        },
      ),
    [EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundFtEmployeeCount]:
      makeGroupFieldValidator(
        'payroll-yr-ft-count',
        (value, parent) => [
          value,
          parent.yearRoundFtAvgHourlyWage,
          parent.yearRoundFtAvgWeeklyHours,
        ],
        (_, parent) => yrPtValues(parent),
        false,
        requiredMessage,
        undefined,
        skipYearRound,
      ),
    [EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundFtAvgHourlyWage]:
      makeGroupFieldValidator(
        'payroll-yr-ft-wage',
        (value, parent) => [
          parent.yearRoundFtEmployeeCount,
          value,
          parent.yearRoundFtAvgWeeklyHours,
        ],
        (_, parent) => yrPtValues(parent),
        false,
        requiredMessage,
        EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundFtEmployeeCount,
        skipYearRound,
      ),
    [EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundFtAvgWeeklyHours]:
      makeGroupFieldValidator(
        'payroll-yr-ft-hours',
        (value, parent) => [
          parent.yearRoundFtEmployeeCount,
          parent.yearRoundFtAvgHourlyWage,
          value,
        ],
        (_, parent) => yrPtValues(parent),
        false,
        requiredMessage,
        EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundFtEmployeeCount,
        skipYearRound,
      ),
    [EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtEmployeeCount]:
      makeGroupFieldValidator(
        'payroll-yr-pt-count',
        (_, parent) => yrFtValues(parent),
        (value, parent) => [
          value,
          parent.yearRoundPtAvgHourlyWage,
          parent.yearRoundPtAvgWeeklyHours,
        ],
        true,
        requiredMessage,
        undefined,
        skipYearRound,
      ),
    [EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtAvgHourlyWage]:
      makeGroupFieldValidator(
        'payroll-yr-pt-wage',
        (_, parent) => yrFtValues(parent),
        (value, parent) => [
          parent.yearRoundPtEmployeeCount,
          value,
          parent.yearRoundPtAvgWeeklyHours,
        ],
        true,
        requiredMessage,
        EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtEmployeeCount,
        skipYearRound,
      ),
    [EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtAvgWeeklyHours]:
      makeGroupFieldValidator(
        'payroll-yr-pt-hours',
        (_, parent) => yrFtValues(parent),
        (value, parent) => [
          parent.yearRoundPtEmployeeCount,
          parent.yearRoundPtAvgHourlyWage,
          value,
        ],
        true,
        requiredMessage,
        EXPANDED_HOTEL_PAYROLL_WORKSHEET_FIELD_CODES.yearRoundPtEmployeeCount,
        skipYearRound,
      ),
  };

  return { ...commonValidations, ...seasonal, ...yearRound };
};
